import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Shield, Save, RefreshCw } from "lucide-react";


/** UI to configure drift auto-pause thresholds on a deployment. */
export function DriftSettingsPanel({ dep, onSaved }) {
  const [enabled, setEnabled] = useState(!!dep.drift_auto_pause);
  const t = { ...defaults(), ...(dep.drift_thresholds || {}) };
  const [minTrades, setMinTrades] = useState(t.min_closed_trades);
  const [wrDrop, setWrDrop] = useState(t.win_rate_drop_pct);
  const [usdDrop, setUsdDrop] = useState(t.net_usd_drop);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/deployments/${dep.deployment_id}`, {
        drift_auto_pause: enabled,
        drift_thresholds: {
          min_closed_trades: Number(minTrades) || 5,
          win_rate_drop_pct: Number(wrDrop) || 20,
          net_usd_drop: Number(usdDrop) || -50,
        },
      });
      onSaved?.(data);
      toast.success(enabled ? "Drift auto-pause armed" : "Drift auto-pause disabled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const paused = dep.pause_reason === "drift_exceeded";

  return (
    <div className="card-sharp p-5 mb-6" data-testid="drift-settings-panel">
      <div className="flex items-center gap-3 mb-4">
        <Shield className="text-[var(--accent)]" size={18}/>
        <div className="flex-1">
          <h2 className="font-display text-lg text-white">Auto-pause safety net</h2>
          <p className="text-[11px] font-mono text-[var(--text-muted)]">
            Pauses the deployment automatically when live P&L drifts too far below its backtest.
            Runs a fresh comparison every ~5 ticks.
          </p>
        </div>
      </div>

      {paused && dep.pause_reason_detail && (
        <div className="border border-[var(--warning)]/40 bg-[var(--warning)]/5 rounded p-3 mb-4 text-xs font-mono text-[var(--warning)]"
          data-testid="drift-pause-banner">
          🛑 Auto-paused: {dep.pause_reason_detail}
        </div>
      )}

      <label className="flex items-center gap-3 mb-4 cursor-pointer">
        <input data-testid="drift-toggle" type="checkbox"
          checked={enabled} onChange={(e)=>setEnabled(e.target.checked)}
          className="accent-[var(--accent)]"/>
        <span className="text-sm font-mono text-[var(--text-primary)]">
          Enable drift auto-pause
        </span>
      </label>

      <div className={`grid grid-cols-3 gap-3 ${enabled ? "" : "opacity-40 pointer-events-none"}`}>
        <ThresholdInput label="Min closed trades" testId="drift-min-trades"
          value={minTrades} onChange={setMinTrades} step="1" min="1"
          hint="Skip check until this many live trades have closed"/>
        <ThresholdInput label="Max win-rate drop (%)" testId="drift-wr-drop"
          value={wrDrop} onChange={setWrDrop} step="1" min="0"
          hint="Pause if live win-rate is this many pct-points below backtest"/>
        <ThresholdInput label="Max USD drop" testId="drift-usd-drop"
          value={usdDrop} onChange={setUsdDrop} step="10"
          hint="Pause if live net USD is this much below backtest (negative)"/>
      </div>

      <div className="mt-5 flex items-center gap-3">
        <button data-testid="drift-save-btn" onClick={save} disabled={busy}
          className="btn-primary flex items-center gap-2">
          {busy ? <RefreshCw size={13} className="animate-spin"/> : <Save size={13}/>} Save settings
        </button>
        <span className="text-[10px] font-mono text-[var(--text-muted)]">
          Changes take effect on the next tick.
        </span>
      </div>
    </div>
  );
}


function ThresholdInput({ label, testId, value, onChange, hint, ...rest }) {
  return (
    <div>
      <label className="label-tiny">{label}</label>
      <input data-testid={testId} type="number" className="input-dark"
        value={value} onChange={(e)=>onChange(e.target.value)} {...rest}/>
      <p className="text-[10px] font-mono text-[var(--text-muted)] mt-1">{hint}</p>
    </div>
  );
}


function defaults() {
  return { min_closed_trades: 5, win_rate_drop_pct: 20, net_usd_drop: -50 };
}
